package fr.manyverse.app.presentation.components.modules.gm_timer

import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Pause
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material.icons.filled.Timer
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.unit.dp
import fr.manyverse.app.domain.model.GmTimer
import fr.manyverse.app.presentation.components.modules.timer.formatCountdown
import kotlinx.coroutines.delay
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.timer_pause
import manyverse.shared.generated.resources.timer_resume
import manyverse.shared.generated.resources.timer_stop
import org.jetbrains.compose.resources.stringResource

private const val CRITICAL_TIME_IN_SECONDS = 30

@Composable
fun GmTimerBubble(
    timer: GmTimer,
    onTick: (timerId: String) -> Unit,
    onPause: () -> Unit,
    onStop: () -> Unit,
    modifier: Modifier = Modifier
) {
    LaunchedEffect(timer.id, timer.isPaused) {
        if (!timer.isPaused) {
            while (true) {
                delay(1000)
                onTick(timer.id)
            }
        }
    }

    val isOvertime = timer.overtimeSeconds > 0
    val isUrgent = !isOvertime && timer.remainingSeconds <= CRITICAL_TIME_IN_SECONDS

    val blinkAlpha = if (isUrgent) {
        val infiniteTransition = rememberInfiniteTransition()
        val animatedAlpha by infiniteTransition.animateFloat(
            initialValue = 1f,
            targetValue = 0.3f,
            animationSpec = infiniteRepeatable(
                animation = tween(500),
                repeatMode = RepeatMode.Reverse
            )
        )
        animatedAlpha
    } else 1f

    val contentColor = when {
        isOvertime -> MaterialTheme.colorScheme.error
        isUrgent -> MaterialTheme.colorScheme.error
        else -> MaterialTheme.colorScheme.onSurface
    }

    val displayText = if (isOvertime)
        "+${formatCountdown(timer.overtimeSeconds)}"
    else
        formatCountdown(timer.remainingSeconds)

    Surface(
        modifier = modifier.graphicsLayer { alpha = blinkAlpha },
        shape = RoundedCornerShape(12.dp),
        color = MaterialTheme.colorScheme.surfaceVariant,
        shadowElevation = 4.dp
    ) {
        Row(
            modifier = Modifier.padding(start = 4.dp, end = 4.dp, top = 4.dp, bottom = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            IconButton(onClick = onPause, modifier = Modifier.size(36.dp)) {
                Icon(
                    imageVector = if (timer.isPaused) Icons.Default.PlayArrow else Icons.Default.Pause,
                    contentDescription = stringResource(if (timer.isPaused) Res.string.timer_resume else Res.string.timer_pause),
                    tint = contentColor,
                    modifier = Modifier.size(20.dp)
                )
            }

            Icon(
                imageVector = Icons.Default.Timer,
                contentDescription = null,
                tint = contentColor,
                modifier = Modifier.size(20.dp)
            )

            Text(
                text = displayText,
                style = MaterialTheme.typography.titleMedium,
                color = contentColor
            )

            IconButton(onClick = onStop, modifier = Modifier.size(36.dp)) {
                Icon(
                    imageVector = Icons.Default.Close,
                    contentDescription = stringResource(Res.string.timer_stop),
                    tint = contentColor,
                    modifier = Modifier.size(20.dp)
                )
            }
        }
    }
}

